/* eslint-disable react-hooks/exhaustive-deps */
import { Auth } from "aws-amplify";
import React, { createContext, useCallback, useContext } from "react";
import UserDTO from "../../data/dtos/UserDTO";
import { api } from "../../infra/http/remote";
import LocalStorageUtils from "../../utils/LocalStorageUtils";
import toastUtils from "../../utils/ToastUtils";

type SignInProps = {
  email: string;
  password: string;
};
type SignUpProps = {
  email: string;
  password: string;
  confirmPassword: string;
};
type ConfirmSignUpProps = {
  email: string;
  code: string;
};

interface IAuthContext {
  signIn: (props: SignInProps) => Promise<any>;
  confirmSignUp: (props: ConfirmSignUpProps) => Promise<void>;
  signUp: (props: SignUpProps) => Promise<UserDTO | undefined>;
}

const AuthContext = createContext<IAuthContext | null>(null);

const AuthProvider: React.FC = ({ children }) => {
  // Filtrar as mensagens de erro do cognito
  const filterError = useCallback((errorMessage: string): any => {
    if (errorMessage.includes("ACCESS_DENIED")) {
      toastUtils.showMessage({
        type: "error",
        message:
          "Não foi possível fazer login, verifique seu acesso ao sistema e tente novamente.",
      });
      return;
    }

    if (errorMessage.includes("Current status is CONFIRMED")) {
      return;
    }

    if (errorMessage.includes("Incorrect username or password")) {
      toastUtils.showMessage({
        type: "error",
        message: "Usuário ou senha incorretos.",
      });
      return;
    }

    if (errorMessage.includes("Password does not conform to policy")) {
      toastUtils.showMessage({
        type: "warning",
        message: "A senha fornecida não atende aos critérios obrigatórios.",
      });
      return;
    }

    if (
      errorMessage.includes(
        "Temporary password has expired and must be reset by an administrator"
      )
    ) {
      toastUtils.showMessage({
        type: "error",
        message:
          "A senha temporária expirou e deve ser redefinida por um administrador.",
      });
      return;
    }

    if (errorMessage.includes("NEW_PASSWORD_REQUIRED")) {
      // navigate("/new-password", { state: { backPage: "/", email, oldPass: password } });
      return;
    }

    if (errorMessage.includes("EMAIL_NOT_VERIFIED")) {
      // navigate("/verify-account", { state: { backPage: "/", email } });
      return;
    }

    if (errorMessage.includes("Register error")) {
      toastUtils.showMessage({
        type: "error",
        message: "Não foi possível criar sua conta, tente novamente.",
      });
      return;
    }

    if (errorMessage.includes("Login error")) {
      toastUtils.showMessage({
        type: "error",
        message: "Não foi possível fazer login, tente novamente.",
      });
      return;
    }

    if (
      errorMessage.includes("An account with the given email already exist")
    ) {
      toastUtils.showMessage({
        type: "error",
        message: "Este e-mail já esta cadastrado.",
      });
      return;
    }

    toastUtils.showMessage({
      type: "error",
      message: errorMessage,
    });
  }, []);

  // Fazer login no cognito
  const signIn = async ({ email, password }: SignInProps): Promise<void> => {
    try {
      if (!email || !password) {
        toastUtils.showMessage({
          type: "error",
          message: "Por favor, preencha todos os campos antes de continuar!",
        });
        return;
      }

      let token = "";
      const user = await Auth.signIn(email, password);
      
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        throw new Error("NEW_PASSWORD_REQUIRED");
      }

      if (user.attributes.email_verified) {
        token = user.signInUserSession.accessToken.jwtToken;
      } else {
        throw new Error("EMAIL_NOT_VERIFIED");
      }

      if (!token || token.length === 0) {
        throw new Error("ACCESS_DENIED");
      }

      const { data }: any = await api.get("/me");

      if (!data.enabled) {
        throw new Error("ACCESS_DENIED");
      }

      // eslint-disable-next-line consistent-return
      return data;
    } catch (error: any) {
      filterError(error.message);
    }
  };
  // Registrar novo usuário
  const signUp = async ({
    email,
    confirmPassword,
    password,
  }: SignUpProps): Promise<any> => {
    try {
      if (password.trim() !== confirmPassword.trim()) {
        toastUtils.showMessage({
          type: "error",
          message: "As senhas não conferem, tente novamente!",
        });
        return;
      }

      const cognitoUser = await Auth.signUp({
        username: email.trim(),
        password,
      });

     

      const { data: user } = await api.post<UserDTO>("/public/register", {
        email: email.trim(),
        name: "",
        username: cognitoUser.userSub,
      });

      LocalStorageUtils.setItem(JSON.stringify(user), "userRegister");
      LocalStorageUtils.setItem(
        JSON.stringify(cognitoUser.user),
        "cognitoUser"
      );

      // eslint-disable-next-line consistent-return
      return user;
    } catch (error: any) {
      console.error("ERROR_SIGNUP_USER_COGNITO: ", error);
      console.error(error?.message);
      if (
        error?.message.includes(
          "An account with the given email already exists."
        ) ||
        error.includes("UsernameExistsException") ||
        error.message.includes("This email is already registered")
      ) {
        toastUtils.showMessage({
          type: "error",
          message: "Este e-mail já esta cadastrado!",
        });
        return;
      }

      filterError(error.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };
  // Confirma o código enviado no email
  const confirmSignUp = useCallback(
    async ({ email, code }: ConfirmSignUpProps) => {
      try {
        await Auth.confirmSignUp(email, code);
        toastUtils.showMessage({
          type: "success",
          message:
            "Sua conta foi ativada. Faça login e comece a usar o sistema.",
        });
      } catch (error: any) {
        filterError(error.message);
      }
    },
    []
  );

  return (
    <AuthContext.Provider value={{ signIn, confirmSignUp, signUp }}>
      {children}
    </AuthContext.Provider>
  );
};

function useAuthContext(): IAuthContext {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("'useAuthContext' must be used within a 'AuthProvider'");
  }

  return context;
}

export { AuthProvider, useAuthContext };
